import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import {
  Avatar,
  Box,
  Typography,
  IconButton,
  InputBase,
  Chip,
  Tab,
  Tabs,
} from '@mui/material';
import {
  West as WestIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

import BottomSheet from '../../../../Components/Ui/BottomSheet';
import { Button } from '../../../../Components/MUIs';
import TimePicker from './TimePicker';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { PermitActions } from '../../../../Reducer/PermitReducer';

export default function ExtendBottomSheet({ SaveLogic1 }) {
  const dispatch = useDispatch();
  const { depApproval, extendData, approvalLine } = useSelector(
    (state) => state.permit
  );
  const {
    intermediateApprovers: _legacyIntermediateApprovers,
    finalApprover: _legacyFinalApprover,
    ...baseExtendData
  } = extendData;
  const { depHeads, safetyManager } = approvalLine;

  const [step, setStep] = useState(0);
  const [selectedEndDate, setSelectedEndDate] = useState(
    dayjs(extendData.workEndDate)
  );

  const [selectedDep, setSelectedDep] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const updateApprovalLine = (nextState) => {
    dispatch(
      PermitActions.setApprovalLine({
        ...nextState,
        smId:
          nextState.smId ??
          nextState.safetyManager?.smId ??
          approvalLine.smId ??
          '',
      })
    );
  };

  const handleEndTimeSelect = (formatted, updated) => {
    let hour = parseInt(updated.hour);
    const minute = parseInt(updated.minute);
    const period = updated.period;

    if (period === 'PM' && hour < 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }

    setSelectedEndDate((prev) => prev.hour(hour).minute(minute).second(0));
  };

  const handleDepClick = (dep) => {
    setSelectedDep(dep);
    setSearchQuery('');
    setActiveTab(0);
  };

  const handleBack = () => {
    if (selectedDep) {
      setSelectedDep(null);
      setSearchQuery('');
    } else {
      setStep(0);
    }
  };

  const filteredManagers = useMemo(() => {
    if (!selectedDep) return [];
    const query = searchQuery.trim().toLowerCase();
    if (!query) return selectedDep.managers || [];
    return (selectedDep.managers || []).filter(
      (m) =>
        m.name?.toLowerCase().includes(query) ||
        m.position?.toLowerCase().includes(query)
    );
  }, [selectedDep, searchQuery]);

  const handlePersonClick = (mem) => {
    if (activeTab === 0) {
      const alreadySelected = depHeads.some((head) => head.headId === mem.id);
      if (alreadySelected) {
        updateApprovalLine({
          depHeads: depHeads.filter((head) => head.headId !== mem.id),
        });
      } else {
        if (depHeads.length >= 4) {
          dispatch(
            showSnackbar({
              message: '중간결재자는 최대 4명까지 선택 가능합니다.',
              type: 'warning',
            })
          );
          return;
        }
        updateApprovalLine({
          depHeads: [
            ...depHeads,
            {
              depCd: selectedDep.depCd,
              depNm: selectedDep.depNm,
              headId: mem.id,
              headNm: mem.name,
              position: mem.position,
              photo: mem.photo,
            },
          ],
        });
      }
    } else {
      const isSame = safetyManager?.smId === mem.id;
      updateApprovalLine({
        safetyManager: isSame
          ? null
          : {
              depCd: selectedDep.depCd,
              depNm: selectedDep.depNm,
              smId: mem.id,
              smNm: mem.name,
              position: mem.position,
              photo: mem.photo,
            },
        smId: isSame ? '' : mem.id,
      });
    }
  };

  const getDepSummary = (dep) => {
    const selectedHeads = depHeads.filter((head) => head.depCd === dep.depCd);
    const isFinalFromDep = safetyManager?.depCd === dep.depCd;
    const lines = [];
    if (selectedHeads.length > 0) {
      lines.push(
        `중간결재자: ${selectedHeads.map((head) => head.headNm).join(', ')}`
      );
    }
    if (isFinalFromDep) {
      lines.push(`최종결재자: ${safetyManager.smNm}`);
    }
    return lines;
  };

  const validateNextStep = () => {
    const originalEnd = dayjs(extendData.workEndDate);
    const diffMinutes = selectedEndDate.diff(originalEnd, 'minute');

    if (diffMinutes <= 0) {
      dispatch(
        showSnackbar({
          message: '종료 시간은 기존보다 이후여야 합니다.',
          type: 'warning',
        })
      );
      return;
    }
    if (diffMinutes > 240) {
      dispatch(
        showSnackbar({
          message: '연장은 최대 4시간까지 가능합니다.',
          type: 'warning',
        })
      );
      return;
    }
    setStep(1);
  };

  const handleConfirm = async () => {
    if (depHeads.length === 0) {
      dispatch(
        showSnackbar({
          message: '중간결재자를 최소 1명 선택해주세요.',
          type: 'warning',
        })
      );
      return;
    }

    if (!safetyManager) {
      dispatch(
        showSnackbar({
          message: '최종결재자를 선택해주세요.',
          type: 'warning',
        })
      );
      return;
    }

    const formattedDate = selectedEndDate.format('YYYY-MM-DD HH:mm');
    const updatedData = {
      ...baseExtendData,
      workEndDate: formattedDate,
      smId: safetyManager.smId,
      depHeads,
      safetyManager,
    };
    dispatch(PermitActions.setExtendData(updatedData));
    dispatch(BottomSheetActions.CloseBottomSheet());
    await SaveLogic1(updatedData);
  };

  const headerTitle = () => {
    if (step === 0) return '연장 신청';
    if (selectedDep) return selectedDep.depNm;
    return '부서 선택';
  };

  return (
    <BottomSheet
      isHeaderChildren
      headerChildren={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {step === 1 && (
            <IconButton onClick={handleBack}>
              <WestIcon sx={{ fontSize: '22px' }} />
            </IconButton>
          )}
          <Typography sx={{ fontSize: '16px', fontWeight: 600 }}>
            {headerTitle()}
          </Typography>
        </Box>
      }
    >
      {step === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ color: '#918A8A', ml: 1, mb: 2 }}>
            종료 시간을 선택해주세요.
          </Typography>
          <TimePicker
            value={{
              hour: selectedEndDate.format('hh'),
              minute: selectedEndDate.format('mm'),
              period: selectedEndDate.hour() >= 12 ? 'PM' : 'AM',
            }}
            onChange={handleEndTimeSelect}
          />
          <Box p={2}>
            <Button
              name="다음"
              onClick={validateNextStep}
              sx={{
                width: '100%',
                height: 48,
                borderRadius: '8px',
                backgroundColor: '#57B84D',
                color: '#fff',
              }}
            />
          </Box>
        </Box>
      )}

      {step === 1 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '500px',
            overflow: 'hidden',
          }}
        >
          {selectedDep && (
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              sx={{
                px: 2,
                flexShrink: 0,
                borderBottom: '1px solid #f0f0f0',
                '& .MuiTabs-indicator': {
                  backgroundColor: activeTab === 0 ? '#57B84D' : '#1976d2',
                },
              }}
            >
              <Tab
                label={`중간결재자 ${depHeads.length}/4`}
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: activeTab === 0 ? '#57B84D' : '#999',
                  '&.Mui-selected': { color: '#57B84D' },
                }}
              />
              <Tab
                label={`최종결재자 ${safetyManager ? '1/1' : '0/1'}`}
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: activeTab === 1 ? '#1976d2' : '#999',
                  '&.Mui-selected': { color: '#1976d2' },
                }}
              />
            </Tabs>
          )}

          {selectedDep && (
            <Box
              sx={{
                mx: 2,
                my: 1,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                px: 1.5,
                py: 0.5,
                backgroundColor: '#fafafa',
              }}
            >
              <SearchIcon sx={{ fontSize: 18, color: '#aaa', mr: 1 }} />
              <InputBase
                placeholder="이름 또는 직급으로 검색"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, fontSize: '14px' }}
              />
              {searchQuery.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => setSearchQuery('')}
                  sx={{ p: 0 }}
                >
                  <CloseIcon sx={{ fontSize: 16, color: '#aaa' }} />
                </IconButton>
              )}
            </Box>
          )}

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {selectedDep ? (
              filteredManagers.length === 0 ? (
                <Typography
                  sx={{
                    textAlign: 'center',
                    color: '#aaa',
                    fontSize: '14px',
                    mt: 4,
                  }}
                >
                  검색 결과가 없습니다.
                </Typography>
              ) : (
                filteredManagers.map((mem) => {
                  const isIntermediate = depHeads.some(
                    (head) => head.headId === mem.id
                  );
                  const isFinal = safetyManager?.smId === mem.id;

                  const isSelected = activeTab === 0 ? isIntermediate : isFinal;

                  return (
                    <button
                      key={mem.id}
                      onClick={() => handlePersonClick(mem)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        borderBottom: '1px solid #f5f5f5',
                        cursor: 'pointer',
                        background: isSelected ? '#f6fbf5' : '#fff',
                        textAlign: 'left',
                      }}
                    >
                      <Avatar
                        src={mem.photo}
                        sx={{ width: 40, height: 40, mr: 1.5, flexShrink: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Typography
                            sx={{ fontSize: '14px', fontWeight: 600 }}
                          >
                            {mem.name}
                          </Typography>
                          {isIntermediate && (
                            <Chip
                              label="중간결재자"
                              size="small"
                              sx={{
                                fontSize: '10px',
                                height: 18,
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                              }}
                            />
                          )}
                          {isFinal && (
                            <Chip
                              label="최종결재자"
                              size="small"
                              sx={{
                                fontSize: '10px',
                                height: 18,
                                backgroundColor: '#e3f2fd',
                                color: '#1565c0',
                              }}
                            />
                          )}
                        </Box>
                        <Typography sx={{ fontSize: '12px', color: '#888' }}>
                          {mem.position}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <CheckIcon
                          sx={{
                            color: activeTab === 0 ? '#57B84D' : '#1976d2',
                            fontSize: 20,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </button>
                  );
                })
              )
            ) : (
              depApproval?.map((dep) => {
                const summaryLines = getDepSummary(dep);
                const hasSelection = summaryLines.length > 0;

                return (
                  <button
                    key={dep.depCd}
                    onClick={() => handleDepClick(dep)}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: 'calc(100% - 32px)',
                      margin: '6px 16px',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: hasSelection
                        ? '1px solid #57B84D'
                        : '1px solid #eee',
                      cursor: 'pointer',
                      background: hasSelection ? '#f6fbf5' : '#fff',
                      textAlign: 'left',
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>
                        {dep.depNm}
                      </Typography>
                      {summaryLines.map((line, idx) => (
                        <Typography
                          key={idx}
                          sx={{ fontSize: '12px', color: '#57B84D', mt: 0.3 }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: '13px', color: '#aaa' }}>
                      ›
                    </Typography>
                  </button>
                );
              })
            )}
          </Box>

          {(depHeads.length > 0 || safetyManager) && (
            <Box
              sx={{
                px: 2,
                py: 1,
                flexShrink: 0,
                backgroundColor: '#f6fbf5',
                borderTop: '1px solid #e8f5e9',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
              }}
            >
              {depHeads.map((head) => (
                <Chip
                  key={`int-${head.headId}`}
                  label={`중간: ${head.headNm}`}
                  size="small"
                  onDelete={() =>
                    updateApprovalLine({
                      depHeads: depHeads.filter((x) => x.headId !== head.headId),
                    })
                  }
                  sx={{
                    fontSize: '11px',
                    backgroundColor: '#e8f5e9',
                    color: '#2e7d32',
                  }}
                />
              ))}
              {safetyManager && (
                <Chip
                  label={`최종: ${safetyManager.smNm}`}
                  size="small"
                  onDelete={() =>
                    updateApprovalLine({ safetyManager: null, smId: '' })
                  }
                  sx={{
                    fontSize: '11px',
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                  }}
                />
              )}
            </Box>
          )}

          <Box
            sx={{
              p: 2,
              flexShrink: 0,
              borderTop: '1px solid #f0f0f0',
              backgroundColor: '#fff',
            }}
          >
            <Button
              name="연장 신청 완료"
              disabled={!safetyManager || depHeads.length === 0}
              onClick={handleConfirm}
              sx={{
                width: '100%',
                height: 48,
                borderRadius: '8px',
                backgroundColor: '#57B84D',
                color: '#fff',
              }}
            />
          </Box>
        </Box>
      )}
    </BottomSheet>
  );
}
